import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Tab,
  Tabs,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import TaskIcon from '@mui/icons-material/Task';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import { TextEditor } from '@src/components/core/text-editor/text-editor';

import { fetchClients } from '@src/api/actions/clients';
import { createTask } from '@src/api/tasks';
import clientInboxApi from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending', color: 'default' },
  { value: 'analyzing', label: 'Analyzing', color: 'info' },
  { value: 'analyzed', label: 'Analyzed', color: 'success' },
  { value: 'task_created', label: 'Task created', color: 'primary' },
  { value: 'ignored', label: 'Ignored', color: 'warning' },
  { value: 'failed', label: 'Failed', color: 'error' },
];

const TASK_FILTER_OPTIONS = [
  { value: 'all', label: 'All emails' },
  { value: 'without', label: 'Needs task' },
  { value: 'with', label: 'Linked to task' },
];

const STATUS_LABEL_MAP = STATUS_OPTIONS.reduce((map, option) => {
  map[option.value] = option.label;
  return map;
}, {});

const STATUS_COLOR_MAP = STATUS_OPTIONS.reduce((map, option) => {
  if (option.color) {
    map[option.value] = option.color;
  }
  return map;
}, {});

function renderSuggestedAction(action, index) {
  if (!action) {
    return null;
  }

  if (typeof action === 'string') {
    return (
      <Box key={`action-${index}`} px={2} py={1} borderRadius={1} bgcolor="grey.100">
        <Typography variant="body2">{action}</Typography>
      </Box>
    );
  }

  if (typeof action === 'object') {
    const { title, summary, description, owner, due_date: dueDate } = action;
    return (
      <Box
        key={`action-${index}`}
        px={2}
        py={1.5}
        borderRadius={1}
        bgcolor="grey.100"
        display="flex"
        flexDirection="column"
        gap={0.5}
      >
        {title ? (
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
        ) : null}
        {summary ? <Typography variant="body2">{summary}</Typography> : null}
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}
        {owner || dueDate ? (
          <Typography variant="caption" color="text.secondary">
            {owner ? `Owner: ${owner}` : ''}
            {owner && dueDate ? ' • ' : ''}
            {dueDate ? `Due: ${dayjs(dueDate).format('MMM D, YYYY')}` : ''}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return null;
}

function getInitialFilters() {
  return {
    search: '',
    status: 'all',
    client: null,
    task: 'all',
  };
}

function formatEmailAddress(address) {
  if (!address) {
    return '';
  }
  if (typeof address === 'string') {
    return address;
  }
  if (typeof address === 'object') {
    if (address.name && address.email) {
      return `${address.name} <${address.email}>`;
    }
    if (address.email) {
      return address.email;
    }
  }
  return String(address);
}

function normalizeAddresses(addresses) {
  if (!Array.isArray(addresses)) {
    return [];
  }
  return addresses.map((address) => formatEmailAddress(address)).filter(Boolean);
}

function parseAddressInput(value) {
  if (!value) {
    return [];
  }
  return value
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

const BUDGET_NOTE_BLOCK_REGEX =
  /<!-- budget-note:start -->[\s\S]*?<!-- budget-note:end -->|<p[^>]*data-budget-note=\"true\"[^>]*>[\s\S]*?<\/p>|<p[^>]*class=\"budget-note-line\"[^>]*>[\s\S]*?<\/p>/gi;
const ELLIPSIS_ARTIFACT_REGEX = /<(p|div|span)[^>]*>\s*(?:\.{3}|…)\s*<\/\1>/gi;

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function upsertBudgetNote(html, note) {
  const base = String(html || '')
    .replace(BUDGET_NOTE_BLOCK_REGEX, '')
    .replace(ELLIPSIS_ARTIFACT_REGEX, '');
  const trimmed = String(note || '').trim();
  if (!trimmed) {
    return base;
  }

  const noteHtml = escapeHtml(trimmed).replace(/\n/g, '<br>');
  const block = [
    '<p class="budget-note-line" style="margin:14px 0 0;font-size:15px;line-height:1.6;color:#374151;">',
    noteHtml,
    '</p>',
  ].join('');

  const anchor = 'If you want to keep the same budget for';
  const anchorIndex = base.indexOf(anchor);
  if (anchorIndex === -1) {
    return `${base}${block}`;
  }

  const insertAt = base.lastIndexOf('<p', anchorIndex);
  if (insertAt === -1) {
    return `${base}${block}`;
  }
  return `${base.slice(0, insertAt)}${block}${base.slice(insertAt)}`;
}


function ClientInboxPage() {
  const message = useMessage();
  const [filters, setFilters] = useState(getInitialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [replyFromValue, setReplyFromValue] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyToValue, setReplyToValue] = useState('');
  const [replyCcValue, setReplyCcValue] = useState('');
  const [senderOptions, setSenderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [creatingBudgetDrafts, setCreatingBudgetDrafts] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [error, setError] = useState('');
  const [budgetClientIds, setBudgetClientIds] = useState([]);
  const [budgetNote, setBudgetNote] = useState('');
  const [inspectLoading, setInspectLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [inspectError, setInspectError] = useState('');
  const [inspectData, setInspectData] = useState(null);
  const [inspectTab, setInspectTab] = useState('sources');


  const selectedMessage = useMemo(
    () => messages.find((item) => item.id === selectedId) || null,
    [messages, selectedId],
  );
  const isBudgetConfirmationThread = useMemo(
    () => Boolean(selectedMessage?.subject?.toLowerCase().includes('budget confirmation')),
    [selectedMessage],
  );

  const draftChanged = useMemo(() => {
    if (!selectedMessage) {
      return false;
    }
    if (isBudgetConfirmationThread) {
      return upsertBudgetNote(draftValue, budgetNote) !== (selectedMessage.reply_draft || '');
    }
    return draftValue !== (selectedMessage.reply_draft || '');
  }, [budgetNote, draftValue, isBudgetConfirmationThread, selectedMessage]);

  const taskFilterValue = filters.task;

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }, []);

  const loadClients = useCallback(() => {
    fetchClients()
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const normalized = (Array.isArray(data) ? data : []).map((client) => ({
          id: String(client.id),
          name: client.name || 'Unnamed client',
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name));
        setClients(normalized);
      })
      .catch((err) => {
        console.error('Failed to load clients for inbox filters', err);
        message.error('Unable to load clients for the inbox filter.');
      });
  }, [message]);

  const loadMessages = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {
      page,
      ordering: '-received_at',
    };
    if (pageSize) {
      params.page_size = pageSize;
    }

    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.status && filters.status !== 'all') {
      params.processing_status = filters.status;
    }
    if (filters.client) {
      params.client = filters.client;
    }
    if (taskFilterValue === 'with') {
      params.has_task = 'true';
    } else if (taskFilterValue === 'without') {
      params.has_task = 'false';
    }

    clientInboxApi
      .listEmailMessages(params)
      .then((response) => {
        const payload = response.data;
        const results = Array.isArray(payload)
          ? payload
          : payload?.results || [];
        const count = Array.isArray(payload)
          ? payload.length
          : payload?.count ?? results.length;

        setMessages(results);
        setTotalCount(count);
        if (Array.isArray(payload)) {
          const derived = results.length || pageSize || 25;
          if (derived !== pageSize) {
            setPageSize(derived);
          }
        } else if (payload?.page_size && payload.page_size !== pageSize) {
          setPageSize(payload.page_size);
        }
        setSelectedId((current) => {
          if (results.length === 0) {
            return null;
          }
          return results.some((item) => item.id === current)
            ? current
            : results[0].id;
        });
      })
      .catch((err) => {
        console.error('Failed to load client inbox messages', err);
        setError('Unable to load client inbox messages.');
        message.error('Unable to load client inbox messages.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filters.client, filters.search, filters.status, message, page, pageSize, taskFilterValue]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    clientInboxApi
      .listEmailSenders()
      .then((response) => {
        const senders = response?.data?.senders || [];
        const defaultSender = response?.data?.default || '';
        setSenderOptions(senders);
        setReplyFromValue((current) => {
          if (current && senders.includes(current)) {
            return current;
          }
          if (defaultSender && senders.includes(defaultSender)) {
            return defaultSender;
          }
          return senders[0] || '';
        });
      })
      .catch((err) => {
        console.error('Failed to load available senders', err);
        setSenderOptions([]);
        message.error('Unable to load available sender addresses.');
      });
  }, [message]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (selectedMessage) {
      const nextDraft = selectedMessage.reply_draft || '';
      setDraftValue(nextDraft);
      setBudgetNote('');
      const baseSubject = selectedMessage.subject || 'Client email';
      setReplySubject(baseSubject);
      const toAddresses = normalizeAddresses(selectedMessage.to_addresses || []);
      const fallbackTo = selectedMessage.from_email || '';
      setReplyToValue(toAddresses.length ? toAddresses.join(', ') : fallbackTo);
      setReplyCcValue(normalizeAddresses(selectedMessage.cc_addresses || []).join(', '));
      if (selectedMessage.subject?.includes('Budget Confirmation')) {
        setReplyFromValue('jack@leadgurus.com');
      }
    } else {
      setDraftValue('');
      setBudgetNote('');
      setReplySubject('');
      setReplyToValue('');
      setReplyCcValue('');
    }
    setInspectData(null);
    setInspectError('');
    setInspectTab('sources');
  }, [selectedMessage]);

  const handleInspectContext = useCallback(
    ({ includePrompt = false, includeBudgetPacing = false } = {}) => {
      if (!selectedMessage) {
        return;
      }
      setInspectLoading(true);
      setInspectError('');
      clientInboxApi
        .inspectEmailThreadContext(selectedMessage.id, {
          include_prompt: includePrompt,
          include_budget_pacing: includeBudgetPacing,
        })
        .then((response) => {
          setInspectData(response.data);
          if (includePrompt) {
            setInspectTab('prompt');
          } else if (includeBudgetPacing) {
            setInspectTab('live');
          } else {
            setInspectTab('sources');
          }
        })
        .catch((err) => {
          console.error('Failed to inspect thread context', err);
          setInspectError('Unable to inspect AI context for this thread.');
          message.error('Unable to inspect AI context for this thread.');
        })
        .finally(() => {
          setInspectLoading(false);
        });
    },
    [message, selectedMessage],
  );

  const handleRegenerateDraft = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    setRegenerateLoading(true);
    setInspectError('');
    clientInboxApi
      .regenerateEmailThreadDraft(selectedMessage.id)
      .then((response) => {
        const payload = response.data || {};
        const updatedThread = payload.thread;
        const inspection = payload.inspection;
        if (updatedThread) {
          setMessages((prev) =>
            prev.map((item) => (item.id === updatedThread.id ? { ...item, ...updatedThread } : item)),
          );
          setDraftValue(updatedThread.reply_draft || '');
        }
        if (inspection) {
          setInspectData(inspection);
          setInspectTab('changes');
          if (inspection?.draft_comparison?.changed) {
            message.success('Draft regenerated with updated AI context.');
          } else {
            message.success('Draft regenerated. Context stayed materially the same.');
          }
        } else {
          message.success('Draft regenerated.');
        }
      })
      .catch((err) => {
        console.error('Failed to regenerate reply draft', err);
        setInspectError('Unable to regenerate the AI draft for this thread.');
        message.error('Unable to regenerate the AI draft for this thread.');
      })
      .finally(() => {
        setRegenerateLoading(false);
      });
  }, [message, selectedMessage]);

  const renderInspectorSection = useCallback(() => {
    if (!inspectData) {
      return (
        <Typography variant="body2" color="text.secondary">
          Load AI Brain Inspector to review sources used, selected knowledge, memory, live data, and the final prompt.
        </Typography>
      );
    }

    if (inspectTab === 'sources') {
      const sources = inspectData.sources_used || {};
      const sections = [
        {
          label: 'Knowledge',
          items: sources.knowledge || [],
          render: (item) =>
            `${item.title}${item.chunk_type ? ` • ${item.chunk_type}` : ''}${
              item.topic ? ` (${item.topic})` : ''
            }`,
        },
        {
          label: 'Memory',
          items: sources.memory || [],
          render: (item) => `#${item.id} ${item.title}${item.category ? ` • ${item.category}` : ''}`,
        },
        {
          label: 'Recent client emails',
          items: sources.recent_relationship_messages || [],
          render: (item) => `#${item.id} ${item.subject}`,
        },
        {
          label: 'Recent reply examples',
          items: sources.recent_reply_examples || [],
          render: (item) => `#${item.id} ${item.subject}`,
        },
      ];

      return (
        <Stack spacing={1.5}>
          {sections.map((section) => (
            <Box key={section.label} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">{section.label}</Typography>
              {section.items.length ? (
                <Stack spacing={0.75} mt={1}>
                  {section.items.map((item) => (
                    <Typography key={item.key || `${section.label}-${item.id}`} variant="body2">
                      {section.render(item)}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No sources selected.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'changes') {
      const sourceChanges = inspectData.source_changes || {};
      const sections = Object.values(sourceChanges.sections || {});
      return (
        <Stack spacing={1.5}>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Draft comparison</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {inspectData.draft_comparison?.changed
                ? 'The regenerated draft changed.'
                : 'The regenerated draft matched the previous version.'}
            </Typography>
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Context diff summary</Typography>
            {(sourceChanges.summary || []).length ? (
              <Stack spacing={0.75} mt={1}>
                {sourceChanges.summary.map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No source changes relative to the previous saved draft.
              </Typography>
            )}
          </Box>
          {sections.map((section) => (
            <Box key={section.label} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">{section.label}</Typography>
              {section.changed ? (
                <Stack spacing={0.75} mt={1}>
                  {(section.added || []).map((item) => (
                    <Typography key={`added-${section.label}-${item}`} variant="body2" color="success.main">
                      + {item}
                    </Typography>
                  ))}
                  {(section.removed || []).map((item) => (
                    <Typography key={`removed-${section.label}-${item}`} variant="body2" color="error.main">
                      - {item}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No changes.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'knowledge') {
      const snippets = inspectData.knowledge_snippets || [];
      if (!snippets.length) {
        return <Typography variant="body2" color="text.secondary">No knowledge snippets selected.</Typography>;
      }
      return (
        <Stack spacing={1.5}>
          {snippets.map((snippet, index) => (
            <Box key={`${snippet.title}-${index}`} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">
                {snippet.title} {snippet.chunk_type ? `• ${snippet.chunk_type}` : ''}
              </Typography>
              {snippet.topic ? (
                <Typography variant="caption" color="text.secondary">
                  Topic: {snippet.topic}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {snippet.content}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'memory') {
      const items = inspectData.memory_items || [];
      if (!items.length) {
        return <Typography variant="body2" color="text.secondary">No memory items selected.</Typography>;
      }
      return (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Box key={item.id} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">
                #{item.id} {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.category} • score {(item.score || 0).toFixed(3)}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {item.summary}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'live') {
      return (
        <Stack spacing={1.5}>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Live snapshot</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {inspectData.sections?.live_data || 'No live data available.'}
            </Typography>
          </Box>
          {inspectData.budget_pacing ? (
            <Box p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">Budget pacing action</Typography>
              <Box
                component="pre"
                sx={{ m: 0, mt: 1, whiteSpace: 'pre-wrap', fontSize: 12, overflow: 'auto' }}
              >
                {JSON.stringify(inspectData.budget_pacing, null, 2)}
              </Box>
            </Box>
          ) : null}
        </Stack>
      );
    }

    if (inspectTab === 'recent') {
      return (
        <Stack spacing={1.5}>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Recent relationship memory</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {inspectData.sections?.recent_relationship_memory || 'No recent relationship memory.'}
            </Typography>
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Recent reply examples</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {inspectData.sections?.recent_reply_examples || 'No recent reply examples.'}
            </Typography>
          </Box>
        </Stack>
      );
    }

    if (inspectTab === 'prompt') {
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'grey.50',
            whiteSpace: 'pre-wrap',
            fontSize: 12,
            maxHeight: 420,
            overflow: 'auto',
          }}
        >
          {inspectData.prompt || 'Prompt not loaded.'}
        </Box>
      );
    }

    return null;
  }, [inspectData, inspectTab]);

  const handleRefresh = useCallback(() => {
    loadMessages();
  }, [loadMessages]);

  const handleSaveDraft = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    const draftToSave = isBudgetConfirmationThread
      ? upsertBudgetNote(draftValue, budgetNote)
      : draftValue;
    setSavingDraft(true);
    clientInboxApi
      .updateEmailMessage(selectedMessage.id, { reply_draft: draftToSave })
      .then((response) => {
        const updated = response.data;
        setMessages((prev) =>
          prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
        );
        if (isBudgetConfirmationThread) {
          setDraftValue(draftToSave);
          setBudgetNote('');
        }
        message.success('Draft saved.');
      })
      .catch((err) => {
        console.error('Failed to save reply draft', err);
        message.error('Unable to save the reply draft.');
      })
      .finally(() => {
        setSavingDraft(false);
      });
  }, [budgetNote, draftValue, isBudgetConfirmationThread, message, selectedMessage]);

  const handleCreateTask = useCallback(() => {
    if (!selectedMessage || selectedMessage.created_task) {
      return;
    }
    setCreatingTask(true);
    const payload = {
      client: selectedMessage.client,
      title: selectedMessage.subject || 'Client email follow-up',
      notes: selectedMessage.summary || '',
      description: selectedMessage.body_text || '',
      status: 'backlog',
      priority: 'medium',
    };

    createTask(payload)
      .then((taskResponse) => {
        const task = taskResponse.data;
        return clientInboxApi
          .updateEmailMessage(selectedMessage.id, {
            created_task: task.id,
            processing_status: 'task_created',
          })
          .then((emailResponse) => {
            const updated = emailResponse.data;
            setMessages((prev) =>
              prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
            );
            message.success('Task created and linked to the email.');
          });
      })
      .catch((err) => {
        console.error('Failed to create a task for the email', err);
        message.error('Unable to create a task for this email.');
      })
      .finally(() => {
        setCreatingTask(false);
      });
  }, [message, selectedMessage]);

  const handleSendReply = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    if (!replyFromValue) {
      message.error('Select a sender before sending.');
      return;
    }
    const bodyToSend = isBudgetConfirmationThread
      ? upsertBudgetNote(draftValue, budgetNote)
      : draftValue;
    const trimmedBody = bodyToSend.trim();
    if (!trimmedBody) {
      message.error('Reply body cannot be empty.');
      return;
    }
    const payload = {
      body: bodyToSend,
      subject: replySubject,
      to: parseAddressInput(replyToValue),
      cc: parseAddressInput(replyCcValue),
      from_email: replyFromValue,
    };
    setSendingReply(true);
    clientInboxApi
      .replyToEmailMessage(selectedMessage.id, payload)
      .then((response) => {
        const updated = response.data;
        setMessages((prev) =>
          prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
        );
        setDraftValue('');
        setBudgetNote('');
        message.success('Reply sent.');
      })
      .catch((err) => {
        console.error('Failed to send reply', err);
        message.error('Unable to send the reply.');
      })
      .finally(() => {
        setSendingReply(false);
      });
  }, [
    budgetNote,
    draftValue,
    isBudgetConfirmationThread,
    message,
    replyCcValue,
    replyFromValue,
    replySubject,
    replyToValue,
    selectedMessage,
  ]);

  const handleCreateBudgetDrafts = useCallback(() => {
    if (!budgetClientIds.length) {
      message.error('Select at least one client to create budget drafts.');
      return;
    }
    setCreatingBudgetDrafts(true);
    clientInboxApi
      .createBudgetConfirmations({ client_ids: budgetClientIds })
      .then((response) => {
        const createdCount = response?.data?.created?.length || 0;
        const skippedCount = response?.data?.skipped?.length || 0;
        message.success(
          `Budget drafts created: ${createdCount}. Skipped: ${skippedCount}.`,
        );
        loadMessages();
      })
      .catch((err) => {
        console.error('Failed to create budget confirmation drafts', err);
        message.error('Unable to create budget confirmation drafts.');
      })
      .finally(() => {
        setCreatingBudgetDrafts(false);
      });
  }, [budgetClientIds, loadMessages, message]);

  const totalPages = useMemo(() => {
    if (!totalCount || !pageSize) {
      return 1;
    }
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [pageSize, totalCount]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Client Inbox</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              to="/tasks"
              startIcon={<TaskIcon />}
            >
              Open Task Board
            </Button>
          </Stack>
        </Stack>

        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 260 }} disabled={creatingBudgetDrafts}>
                <InputLabel>Budget confirmation clients</InputLabel>
                <Select
                  multiple
                  label="Budget confirmation clients"
                  value={budgetClientIds}
                  onChange={(event) => setBudgetClientIds(event.target.value)}
                  renderValue={(selected) =>
                    clients
                      .filter((client) => selected.includes(client.id))
                      .map((client) => client.name)
                      .join(', ')
                  }
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleCreateBudgetDrafts}
                disabled={creatingBudgetDrafts}
              >
                {creatingBudgetDrafts ? 'Creating drafts...' : 'Create budget drafts'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                Skips clients that already have approved budgets for next month.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  value={filters.search}
                  onChange={(event) => handleFilterChange('search', event.target.value)}
                  placeholder="Search subject or summary"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(event) => handleFilterChange('status', event.target.value)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Client</InputLabel>
                  <Select
                    label="Client"
                    value={filters.client ?? 'all'}
                    onChange={(event) => {
                      const value = event.target.value;
                      handleFilterChange('client', value === 'all' ? null : value);
                    }}
                  >
                    <MenuItem value="all">All clients</MenuItem>
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Task link</InputLabel>
                  <Select
                    label="Task link"
                    value={taskFilterValue}
                    onChange={(event) => handleFilterChange('task', event.target.value)}
                  >
                    {TASK_FILTER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                    <CircularProgress size={32} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box px={3} py={6} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      No emails match the current filters.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {messages.map((item) => {
                      const addresses = normalizeAddresses(item.to_addresses || []);
                      const initials = item.from_email ? item.from_email.charAt(0).toUpperCase() : '?';
                      return (
                        <ListItemButton
                          key={item.id}
                          onClick={() => setSelectedId(item.id)}
                          selected={item.id === selectedId}
                          alignItems="flex-start"
                        >
                          <ListItemAvatar>
                            <Avatar>{initials}</Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle2" noWrap>
                                  {item.subject || 'Untitled email'}
                                </Typography>
                                {item.processing_status ? (
                                  <Chip
                                    size="small"
                                    label={
                                      STATUS_LABEL_MAP[item.processing_status] ||
                                      item.processing_status
                                    }
                                    color={STATUS_COLOR_MAP[item.processing_status] || 'default'}
                                  />
                                ) : null}
                              </Stack>
                            }
                            secondary={
                              <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  From {item.from_email}
                                </Typography>
                                {addresses.length > 0 ? (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    To {addresses.join(', ')}
                                  </Typography>
                                ) : null}
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {item.summary || item.body_text || 'No summary available'}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                  {item.received_at
                                    ? dayjs(item.received_at).format('MMM D, YYYY h:mm A')
                                    : 'Unknown received time'}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </CardContent>
              {totalPages > 1 ? (
                <Box px={2} py={2} display="flex" justifyContent="center">
                  <Pagination
                    color="primary"
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              ) : null}
            </Card>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Card sx={{ minHeight: 480 }}>
              <CardContent>
                {!selectedMessage ? (
                  <Box py={10} textAlign="center">
                    <Typography variant="body1" color="text.secondary">
                      Select an email on the left to review its analysis and reply draft.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <Box>
                      <Stack spacing={0.5}>
                        <Typography variant="h5">{selectedMessage.subject || 'Untitled email'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          From {selectedMessage.from_email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Received{' '}
                          {selectedMessage.received_at
                            ? dayjs(selectedMessage.received_at).format('MMM D, YYYY h:mm A')
                            : 'Unknown'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {selectedMessage.processing_status ? (
                            <Chip
                              label={
                                STATUS_LABEL_MAP[selectedMessage.processing_status] ||
                                selectedMessage.processing_status
                              }
                              color={
                                STATUS_COLOR_MAP[selectedMessage.processing_status] || 'default'
                              }
                              size="small"
                            />
                          ) : null}
                          {typeof selectedMessage.llm_confidence === 'number' ? (
                            <Chip
                              label={`Confidence ${(selectedMessage.llm_confidence * 100).toFixed(0)}%`}
                              size="small"
                              color="secondary"
                            />
                          ) : null}
                          {selectedMessage.intent ? (
                            <Chip label={`Intent: ${selectedMessage.intent}`} size="small" />
                          ) : null}
                          {selectedMessage.created_task_title ? (
                            <Tooltip title="Open the associated task">
                              <Chip
                                label={`Task: ${selectedMessage.created_task_title}`}
                                size="small"
                                color="primary"
                              />
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </Stack>
                    </Box>

                    <Divider />

                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1">Summary</Typography>
                      <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedMessage.summary || 'No summary available.'}
                      </Typography>
                    </Stack>

                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1">Suggested actions</Typography>
                      {Array.isArray(selectedMessage.suggested_actions) &&
                      selectedMessage.suggested_actions.length > 0 ? (
                        <Stack spacing={1}>
                          {selectedMessage.suggested_actions.map((action, index) =>
                            renderSuggestedAction(action, index),
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No suggested actions provided by the analysis.
                        </Typography>
                      )}
                    </Stack>

                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1">AI Brain Inspector</Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleInspectContext()}
                            disabled={inspectLoading}
                          >
                            {inspectLoading ? 'Loading…' : 'Load context'}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleRegenerateDraft}
                            disabled={regenerateLoading}
                          >
                            {regenerateLoading ? 'Regenerating…' : 'Regenerate draft'}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleInspectContext({ includeBudgetPacing: true })}
                            disabled={inspectLoading}
                          >
                            Pacing
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleInspectContext({ includePrompt: true })}
                            disabled={inspectLoading}
                          >
                            Prompt
                          </Button>
                        </Stack>
                      </Stack>
                      {inspectError ? <Alert severity="error">{inspectError}</Alert> : null}
                      <Tabs
                        value={inspectTab}
                        onChange={(_, value) => setInspectTab(value)}
                        variant="scrollable"
                        allowScrollButtonsMobile
                      >
                        <Tab value="sources" label="Sources" />
                        <Tab value="changes" label="Changes" />
                        <Tab value="knowledge" label="Knowledge" />
                        <Tab value="memory" label="Memory" />
                        <Tab value="live" label="Live Data" />
                        <Tab value="recent" label="Recent" />
                        <Tab value="prompt" label="Prompt" />
                      </Tabs>
                      {renderInspectorSection()}
                    </Stack>

                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1">Reply draft</Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveDraft}
                            disabled={!draftChanged || savingDraft}
                          >
                            {savingDraft ? 'Saving…' : 'Save draft'}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SendIcon />}
                            onClick={handleSendReply}
                            disabled={sendingReply || !draftValue.trim() || !replyFromValue}
                          >
                            {sendingReply ? 'Sending…' : 'Send reply'}
                          </Button>
                        </Stack>
                      </Stack>
                      <Stack spacing={1}>
                        <TextField
                          select
                          label="From"
                          value={replyFromValue}
                          onChange={(event) => setReplyFromValue(event.target.value)}
                          helperText={
                            senderOptions.length === 0
                              ? 'No sender addresses configured'
                              : 'Choose which inbox to send from'
                          }
                          fullWidth
                          disabled={senderOptions.length === 0}
                        >
                          {senderOptions.map((sender) => (
                            <MenuItem key={sender} value={sender}>
                              {sender}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="To"
                          value={replyToValue}
                          onChange={(event) => setReplyToValue(event.target.value)}
                          helperText="Separate multiple emails with commas"
                          fullWidth
                        />
                        <TextField
                          label="CC"
                          value={replyCcValue}
                          onChange={(event) => setReplyCcValue(event.target.value)}
                          helperText="Optional"
                          fullWidth
                        />
                        <TextField
                          label="Subject"
                          value={replySubject}
                          onChange={(event) => setReplySubject(event.target.value)}
                          fullWidth
                        />
                      </Stack>
                      {isBudgetConfirmationThread ? (
                        <Stack spacing={1} mt={1}>
                          <Alert severity="info">
                            Budget template is shown as sent to preserve exact HTML formatting.
                          </Alert>
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1.5,
                              bgcolor: 'grey.50',
                              maxHeight: 380,
                              overflow: 'auto',
                            }}
                            dangerouslySetInnerHTML={{ __html: draftValue }}
                          />
                          <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Add text under budget table (optional)"
                            value={budgetNote}
                            onChange={(event) => setBudgetNote(event.target.value)}
                            placeholder="Type your additional line(s)..."
                          />
                        </Stack>
                      ) : (
                        <TextEditor
                          content={draftValue}
                          onUpdate={({ editor }) => setDraftValue(editor.getHTML())}
                          placeholder="Draft reply"
                        />
                      )}
                    </Stack>

                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1">Original message</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {normalizeAddresses(selectedMessage.to_addresses).length > 0
                          ? `To: ${normalizeAddresses(selectedMessage.to_addresses).join(', ')}`
                          : 'To: —'}
                      </Typography>
                      {normalizeAddresses(selectedMessage.cc_addresses).length > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          CC: {normalizeAddresses(selectedMessage.cc_addresses).join(', ')}
                        </Typography>
                      ) : null}
                      <Box
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          p: 2,
                          maxHeight: 240,
                          overflow: 'auto',
                        }}
                      >
                        <Typography variant="body2" component="span">
                          {selectedMessage.body_text || 'No body text available.'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<TaskIcon />}
                        onClick={handleCreateTask}
                        disabled={Boolean(selectedMessage.created_task) || creatingTask}
                      >
                        {selectedMessage.created_task
                          ? 'Task already linked'
                          : creatingTask
                          ? 'Creating task…'
                          : 'Create task from email'}
                      </Button>
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to="/tasks"
                        startIcon={<TaskIcon />}
                      >
                        View on task board
                      </Button>
                    </Stack>

                    {selectedMessage.processing_notes ? (
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle1">Processing notes</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedMessage.processing_notes}
                        </Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}

export default ClientInboxPage;
